import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import IconButton from './IconButton'
import './ActionMenu.css'

const ACTION_MENU_OPEN_EVENT = 'action-menu-open'
const PANEL_GAP = 4
const VIEWPORT_MARGIN = 8
const PAGE_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

function getEnabledMenuItems(panel) {
  return Array.from(panel?.querySelectorAll('.action-menu__item:not(:disabled)') || [])
}

function getAdjacentPageControl(trigger, backwards) {
  const controls = Array.from(document.querySelectorAll(PAGE_FOCUSABLE_SELECTOR))
    .filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0)
  const triggerIndex = controls.indexOf(trigger)
  if (triggerIndex < 0) return null
  return controls[triggerIndex + (backwards ? -1 : 1)] || null
}

export default function ActionMenu({ actions = [], label = 'Aktionen', className = '' }) {
  const menuRef = useRef(null)
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const menuInstanceRef = useRef(Symbol('action-menu'))
  const initialFocusRef = useRef('first')
  const panelId = useId()
  const [open, setOpen] = useState(false)
  const [panelPosition, setPanelPosition] = useState(null)
  const availableActions = actions.filter(Boolean)

  useEffect(() => {
    const closeOtherMenu = (event) => {
      if (event.detail !== menuInstanceRef.current) setOpen(false)
    }

    document.addEventListener(ACTION_MENU_OPEN_EVENT, closeOtherMenu)
    return () => document.removeEventListener(ACTION_MENU_OPEN_EVENT, closeOtherMenu)
  }, [])

  useEffect(() => {
    if (!open) return undefined

    const focusFrame = window.requestAnimationFrame(() => {
      const menuItems = getEnabledMenuItems(panelRef.current)
      const initialItem = initialFocusRef.current === 'last' ? menuItems[menuItems.length - 1] : menuItems[0]
      initialItem?.focus()
      initialFocusRef.current = 'first'
    })

    const closeOnOutsidePointerDown = (event) => {
      if (menuRef.current?.contains(event.target) || panelRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const closeOnScroll = () => setOpen(false)
    const closeOnResize = () => setOpen(false)
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', closeOnOutsidePointerDown)
    document.addEventListener('keydown', closeOnEscape)
    document.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('resize', closeOnResize)
    return () => {
      window.cancelAnimationFrame(focusFrame)
      document.removeEventListener('pointerdown', closeOnOutsidePointerDown)
      document.removeEventListener('keydown', closeOnEscape)
      document.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('resize', closeOnResize)
    }
  }, [open])

  const openMenu = (initialFocus = 'first') => {
    initialFocusRef.current = initialFocus
    document.dispatchEvent(new CustomEvent(ACTION_MENU_OPEN_EVENT, { detail: menuInstanceRef.current }))
    setOpen(true)
  }

  const focusMenuItem = (position) => {
    const menuItems = getEnabledMenuItems(panelRef.current)
    if (!menuItems.length) return
    const currentIndex = menuItems.indexOf(document.activeElement)
    let nextIndex
    if (position === 'first') nextIndex = 0
    else if (position === 'last') nextIndex = menuItems.length - 1
    else if (position === 'previous') nextIndex = currentIndex <= 0 ? menuItems.length - 1 : currentIndex - 1
    else nextIndex = currentIndex < 0 || currentIndex === menuItems.length - 1 ? 0 : currentIndex + 1
    menuItems[nextIndex]?.focus()
  }

  const handleMenuKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      focusMenuItem('next')
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      focusMenuItem('previous')
    } else if (event.key === 'Home') {
      event.preventDefault()
      focusMenuItem('first')
    } else if (event.key === 'End') {
      event.preventDefault()
      focusMenuItem('last')
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      triggerRef.current?.focus()
    } else if (event.key === 'Tab') {
      event.preventDefault()
      const nextControl = getAdjacentPageControl(triggerRef.current, event.shiftKey)
      setOpen(false)
      window.requestAnimationFrame(() => (nextControl || triggerRef.current)?.focus())
    }
  }

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) {
      setPanelPosition(null)
      return
    }

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const panelRect = panelRef.current.getBoundingClientRect()
    const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - panelRect.width - VIEWPORT_MARGIN)
    const left = Math.min(Math.max(VIEWPORT_MARGIN, triggerRect.right - panelRect.width), maxLeft)
    const fitsBelow = triggerRect.bottom + PANEL_GAP + panelRect.height <= window.innerHeight - VIEWPORT_MARGIN
    const top = fitsBelow
      ? triggerRect.bottom + PANEL_GAP
      : Math.max(VIEWPORT_MARGIN, triggerRect.top - panelRect.height - PANEL_GAP)

    setPanelPosition({ top, left })
  }, [open, availableActions.length])

  if (!availableActions.length) return null

  return (
    <>
      <div
        ref={menuRef}
        className={`action-menu${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
        onClick={(event) => event.stopPropagation()}
      >
        <IconButton
          ref={triggerRef}
          className="action-menu__trigger"
          icon="menu"
          label={label}
          variant="quiet"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onKeyDown={(event) => {
            if (event.key === 'Tab' && open) {
              setOpen(false)
              return
            }
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
            event.preventDefault()
            if (open) {
              focusMenuItem(event.key === 'ArrowUp' ? 'last' : 'first')
            } else {
              openMenu(event.key === 'ArrowUp' ? 'last' : 'first')
            }
          }}
          onClick={() => {
            if (open) setOpen(false)
            else openMenu()
          }}
        />
      </div>
      {open && createPortal(<div
        ref={panelRef}
        id={panelId}
        className="action-menu__panel"
        role="menu"
        style={panelPosition ? { top: panelPosition.top, left: panelPosition.left } : undefined}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleMenuKeyDown}
      >
        {availableActions.map(({ key, label: actionLabel, onClick, disabled = false, tone = '' }) => (
          <button
            className={`action-menu__item${tone ? ` is-${tone}` : ''}`}
            type="button"
            key={key || actionLabel}
            role="menuitem"
            tabIndex={-1}
            data-i18n-live="1"
            onClick={() => {
              setOpen(false)
              triggerRef.current?.focus()
              onClick()
            }}
            disabled={disabled}
          >
            {actionLabel}
          </button>
        ))}
      </div>, document.body)}
    </>
  )
}
